import { Conversation, EnterpriseGPTApiV1Client, ImprovePromptResponse, ImprovePromptRequest, FileParameter, GptConfiguration, ConversationPage } from "./EnterpriseGPTApiV1Client";
import { isAxiosError, AxiosError, AxiosResponse, AxiosRequestConfig } from "axios";

// Utility to add cache-busting timestamp to URLs
function addTimestampToUrl(url: string): string {
    if (url.includes("?")) {
        return url + (url.endsWith("?") || url.endsWith("&") ? "" : "&") + "t=" + Date.now();
    } else {
        return url + "?t=" + Date.now();
    }
}

export class EnterpriseGPTApiV1ClientWrapper extends EnterpriseGPTApiV1Client {
    // Override only methods that need ?t=${Date.now()} to prevent caching problems
    // This is useful for GET requests that might be cached by the browser or proxies.

    getClientConfigurationsOptions(signal?: AbortSignal) {
        let url_ = this.baseUrl + "/api/v1/clientconfigurations";
        url_ = addTimestampToUrl(url_);
        url_ = url_.replace(/[?&]$/, "");
        const options_: AxiosRequestConfig = {
            method: "GET",
            url: url_,
            headers: { "Accept": "text/plain" },
            signal
        };
        return this.instance.request(options_)
            .catch((_error: unknown) => {
                if (isAxiosError(_error) && _error.response) {
                    return _error.response;
                } else {
                    throw _error;
                }
            })
            .then((_response: AxiosResponse) => {
                return this.processGetClientConfigurationsOptions(_response);
            });
    }

    getSpeechServiceToken(signal?: AbortSignal) {
        let url_ = this.baseUrl + "/api/v1/clientconfigurations/authorization/speech-service";
        url_ = addTimestampToUrl(url_);
        url_ = url_.replace(/[?&]$/, "");
        const options_: AxiosRequestConfig = {
            method: "GET",
            url: url_,
            headers: { "Accept": "text/plain" },
            signal
        };
        return this.instance.request(options_)
            .catch((_error: unknown) => {
                if (isAxiosError(_error) && _error.response) {
                    return _error.response;
                } else {
                    throw _error;
                }
            })
            .then((_response: AxiosResponse) => {
                return this.processGetSpeechServiceToken(_response);
            });
    }

    getConversationsPaged(projectId?: string | undefined, 
                          cursor?: string | undefined, 
                          limit?: number | undefined, 
                          fullLoad?: boolean | undefined, 
                          signal?: AbortSignal): Promise<ConversationPage> {
        //console.log("loading ", limit, " conversations");
        let url_ = this.baseUrl + "/api/v1/conversations/paged?";
        if (projectId !== undefined)
            url_ += "projectId=" + encodeURIComponent("" + projectId) + "&";
        if (cursor !== undefined)
            url_ += "cursor=" + encodeURIComponent("" + cursor) + "&";
        if (limit !== undefined)
            url_ += "limit=" + encodeURIComponent("" + limit) + "&";
        if (fullLoad !== undefined)
            url_ += "fullLoad=" + encodeURIComponent("" + fullLoad) + "&";
        url_ = addTimestampToUrl(url_);
        url_ = url_.replace(/[?&]$/, "");
        const options_: AxiosRequestConfig = {
            method: "GET",
            url: url_,
            headers: { "Accept": "application/json" },
            signal
        };
        return this.instance.request(options_)
            .catch((_error: unknown) => {
                if (isAxiosError(_error) && _error.response) {
                    return _error.response;
                } else {
                    throw _error;
                }
            })
            .then((_response: AxiosResponse) => {
                return this.processGetConversationsPaged(_response);
            });
    }

    getConversationsFiltered(searchText?: string | undefined, fullLoad?: boolean | undefined, signal?: AbortSignal) {
        let url_ = this.baseUrl + "/api/v1/conversations/filtered?";
        if (searchText !== undefined)
            url_ += "searchText=" + encodeURIComponent("" + searchText) + "&";
        if (fullLoad !== undefined)
            url_ += "fullLoad=" + encodeURIComponent("" + fullLoad) + "&";
        url_ = addTimestampToUrl(url_);
        url_ = url_.replace(/[?&]$/, "");
        const options_: AxiosRequestConfig = {
            method: "GET",
            url: url_,
            headers: { "Accept": "application/json" },
            signal
        };
        return this.instance.request(options_)
            .catch((_error: unknown) => {
                if (isAxiosError(_error) && _error.response) {
                    return _error.response;
                } else {
                    throw _error;
                }
            })
            .then((_response: AxiosResponse) => {
                return this.processGetConversationsFiltered(_response);
            });
    }

    getNumberOfChatsByGPTForUser(signal?: AbortSignal) {
        let url_ = this.baseUrl + "/api/v1/conversations/numberofchatsbygpt";
        url_ = addTimestampToUrl(url_);
        url_ = url_.replace(/[?&]$/, "");
        const options_: AxiosRequestConfig = {
            method: "GET",
            url: url_,
            headers: { "Accept": "application/json" },
            signal
        };
        return this.instance.request(options_)
            .catch((_error: unknown) => {
                if (isAxiosError(_error) && _error.response) {
                    return _error.response;
                } else {
                    throw _error;
                }
            })
            .then((_response: AxiosResponse) => {
                return this.processGetNumberOfChatsByGPTForUser(_response);
            });
    }

    getConversationById(id: string, signal?: AbortSignal): Promise<Conversation> {
        let url_ = this.baseUrl + "/api/v1/conversations/" + encodeURIComponent("" + id);
        url_ = addTimestampToUrl(url_);
        url_ = url_.replace(/[?&]$/, "");
        const options_: AxiosRequestConfig = {
            method: "GET",
            url: url_,
            headers: { "Accept": "text/plain" },
            signal
        };
        return this.instance.request(options_)
            .catch((_error: unknown) => {
                if (isAxiosError(_error) && _error.response) {
                    return _error.response;
                } else {
                    throw _error;
                }
            })
            .then((_response: AxiosResponse) => {
                return this.processGetConversationById(_response);
            });
    }

    getAttachmentSasURL(id: string, fileName: string, signal?: AbortSignal) {
        let url_ = this.baseUrl + "/api/v1/conversations/" + encodeURIComponent("" + id) + "/attachment/" + encodeURIComponent("" + fileName);
        url_ = addTimestampToUrl(url_);
        url_ = url_.replace(/[?&]$/, "");
        const options_: AxiosRequestConfig = {
            method: "GET",
            url: url_,
            headers: { "Accept": "text/plain" },
            signal
        };
        return this.instance.request(options_)
            .catch((_error: unknown) => {
                if (isAxiosError(_error) && _error.response) {
                    return _error.response;
                } else {
                    throw _error;
                }
            })
            .then((_response: AxiosResponse) => {
                return this.processGetAttachmentSasURL(_response);
            });
    }

    downloadAttachment(id: string, fileName: string, signal?: AbortSignal) {
        let url_ = this.baseUrl + "/api/v1/conversations/" + encodeURIComponent("" + id) + "/attachment/" + encodeURIComponent("" + fileName) + "/download";
        url_ = addTimestampToUrl(url_);
        url_ = url_.replace(/[?&]$/, "");
        const options_: AxiosRequestConfig = {
            responseType: "blob",
            method: "GET",
            url: url_,
            headers: { "Accept": "text/plain" },
            signal
        };
        return this.instance.request(options_)
            .catch((_error: unknown) => {
                if (isAxiosError(_error) && _error.response) {
                    return _error.response;
                } else {
                    throw _error;
                }
            })
            .then((_response: AxiosResponse) => {
                return this.processDownloadAttachment(_response);
            });
    }

    downloadDocument(id: string, documentUrl?: string, signal?: AbortSignal) {
        let url_ = this.baseUrl + "/api/v1/conversations/" + encodeURIComponent("" + id) + "/document/download";
        url_ = addTimestampToUrl(url_);
        if (documentUrl !== undefined) {
            url_ += "&documentUrl=" + encodeURIComponent("" + documentUrl);
        }
        url_ = url_.replace(/[?&]$/, "");
        const options_: AxiosRequestConfig = {
            responseType: "blob",
            method: "GET",
            url: url_,
            headers: { "Accept": "text/plain" },
            signal
        };
        return this.instance.request(options_)
            .catch((_error: unknown) => {
                if (isAxiosError(_error) && _error.response) {
                    return _error.response;
                } else {
                    throw _error;
                }
            })
            .then((_response: AxiosResponse) => {
                return this.processDownloadDocument(_response);
            });
    }

    getGptConfigurations(includeDefaultGpts?: boolean, signal?: AbortSignal) {
        let url_ = this.baseUrl + "/api/v1/gpt";
        url_ = addTimestampToUrl(url_);
        if (includeDefaultGpts !== undefined) {
            url_ += "&includeDefaultGpts=" + encodeURIComponent("" + includeDefaultGpts);
        }
        url_ = url_.replace(/[?&]$/, "");
        const options_: AxiosRequestConfig = {
            method: "GET",
            url: url_,
            headers: { "Accept": "text/plain" },
            signal
        };
        return this.instance.request(options_)
            .catch((_error: unknown) => {
                if (isAxiosError(_error) && _error.response) {
                    return _error.response;
                } else {
                    throw _error;
                }
            })
            .then((_response: AxiosResponse) => {
                return this.processGetGptConfigurations(_response);
            });
    }

    getPersonalGpts(signal?: AbortSignal) {
        let url_ = this.baseUrl + "/api/v1/gpt/personal";
        url_ = addTimestampToUrl(url_);
        url_ = url_.replace(/[?&]$/, "");
        const options_: AxiosRequestConfig = {
            method: "GET",
            url: url_,
            headers: { "Accept": "text/plain" },
            signal
        };
        return this.instance.request(options_)
            .catch((_error: unknown) => {
                if (isAxiosError(_error) && _error.response) {
                    return _error.response;
                } else {
                    throw _error;
                }
            })
            .then((_response: AxiosResponse) => {
                return this.processGetPersonalGpts(_response);
            });
    }

    getSharedGpts(includeInstruction?: boolean | undefined, signal?: AbortSignal) {
        let url_ = this.baseUrl + "/api/v1/gpt/personal/sharedgpts?";
        if (includeInstruction !== undefined)
            url_ += "includeInstruction=" + encodeURIComponent("" + includeInstruction) + "&";
        url_ = addTimestampToUrl(url_);
        url_ = url_.replace(/[?&]$/, "");
        const options_: AxiosRequestConfig = {
            method: "GET",
            url: url_,
            headers: { "Accept": "application/json" },
            signal
        };
        return this.instance.request(options_)
            .catch((_error: unknown) => {
                if (isAxiosError(_error) && _error.response) {
                    return _error.response;
                } else {
                    throw _error;
                }
            })
            .then((_response: AxiosResponse) => {
                return this.processGetSharedGpts(_response);
            });
    }

    getSharedGpt(id: string, signal?: AbortSignal) {
        let url_ = this.baseUrl + "/api/v1/gpt/personal/sharedgpt/{id}";
        if (id === undefined || id === null)
            throw new Error("The parameter 'id' must be defined.");
        url_ = url_.replace("{id}", encodeURIComponent("" + id));
        url_ = addTimestampToUrl(url_);
        url_ = url_.replace(/[?&]$/, "");
        const options_: AxiosRequestConfig = {
            method: "GET",
            url: url_,
            headers: { "Accept": "application/json" },
            signal
        };
        return this.instance.request(options_)
            .catch((_error: unknown) => {
                if (isAxiosError(_error) && _error.response) {
                    return _error.response;
                } else {
                    throw _error;
                }
            })
            .then((_response: AxiosResponse) => {
                return this.processGetSharedGpt(_response);
            });
    }

    isUserEnrolled(signal?: AbortSignal) {
        let url_ = this.baseUrl + "/api/v1/enrollment";
        url_ = addTimestampToUrl(url_);
        url_ = url_.replace(/[?&]$/, "");
        const options_: AxiosRequestConfig = {
            method: "GET",
            url: url_,
            headers: {},
            signal
        };
        return this.instance.request(options_)
            .catch((_error: unknown) => {
                if (isAxiosError(_error) && _error.response) {
                    return _error.response;
                } else {
                    throw _error;
                }
            })
            .then((_response: AxiosResponse) => {
                return this.processIsUserEnrolled(_response);
            });
    }

    getUserPreferences(signal?: AbortSignal) {
        let url_ = this.baseUrl + "/api/v1/user/preferences";
        url_ = addTimestampToUrl(url_);
        url_ = url_.replace(/[?&]$/, "");
        const options_: AxiosRequestConfig = {
            method: "GET",
            url: url_,
            headers: { "Accept": "text/plain" },
            signal
        };
        return this.instance.request(options_)
            .catch((_error: unknown) => {
                if (isAxiosError(_error) && _error.response) {
                    return _error.response;
                } else {
                    throw _error;
                }
            })
            .then((_response: AxiosResponse) => {
                return this.processGetUserPreferences(_response);
            });
    }

    getPersonalGpt(id: string, signal?: AbortSignal) {
        let url_ = this.baseUrl + "/api/v1/gpt/personal/{id}";
        if (id === undefined || id === null)
            throw new Error("The parameter 'id' must be defined.");
        url_ = url_.replace("{id}", encodeURIComponent("" + id));
        url_ = addTimestampToUrl(url_);
        url_ = url_.replace(/[?&]$/, "");
        const options_: AxiosRequestConfig = {
            method: "GET",
            url: url_,
            headers: { "Accept": "text/plain" },
            signal
        };
        return this.instance.request(options_)
            .catch((_error: unknown) => {
                if (isAxiosError(_error) && _error.response) {
                    return _error.response;
                } else {
                    throw _error;
                }
            })
            .then((_response: AxiosResponse) => {
                return this.processGetPersonalGpt(_response);
            });
    }

    anonymous(signal?: AbortSignal) {
        let url_ = this.baseUrl + "/";
        url_ = addTimestampToUrl(url_);
        url_ = url_.replace(/[?&]$/, "");
        const options_: AxiosRequestConfig = {
            method: "GET",
            url: url_,
            headers: {},
            signal
        };
        return this.instance.request(options_)
            .catch((_error: unknown) => {
                if (isAxiosError(_error) && _error.response) {
                    return _error.response;
                } else {
                    throw _error;
                }
            })
            .then((_response: AxiosResponse) => {
                return this.processAnonymous(_response);
            });
    }

    getTermsOfUse(signal?: AbortSignal) {
        let url_ = this.baseUrl + "/api/v1/legal/terms";
        url_ = addTimestampToUrl(url_);
        url_ = url_.replace(/[?&]$/, "");
        const options_: AxiosRequestConfig = {
            method: "GET",
            url: url_,
            headers: { "Accept": "text/plain" },
            signal
        };
        return this.instance.request(options_)
            .catch((_error: unknown) => {
                if (isAxiosError(_error) && _error.response) {
                    return _error.response;
                } else {
                    throw _error;
                }
            })
            .then((_response: AxiosResponse) => {
                return this.processGetTermsOfUse(_response);
            });
    }

    getUserQuota(signal?: AbortSignal) {
        let url_ = this.baseUrl + "/api/v1/quota";
        url_ = addTimestampToUrl(url_);
        url_ = url_.replace(/[?&]$/, "");
        const options_: AxiosRequestConfig = {
            method: "GET",
            url: url_,
            headers: { "Accept": "text/plain" },
            signal
        };
        return this.instance.request(options_)
            .catch((_error: unknown) => {
                if (isAxiosError(_error) && _error.response) {
                    return _error.response;
                } else {
                    throw _error;
                }
            })
            .then((_response: AxiosResponse) => {
                return this.processGetUserQuota(_response);
            });
    }

    getActiveSurvey(signal?: AbortSignal) {
        let url_ = this.baseUrl + "/api/v1/survey/active";
        url_ = addTimestampToUrl(url_);
        url_ = url_.replace(/[?&]$/, "");
        const options_: AxiosRequestConfig = {
            method: "GET",
            url: url_,
            headers: { "Accept": "text/plain" },
            signal
        };
        return this.instance.request(options_)
            .catch((_error: unknown) => {
                if (isAxiosError(_error) && _error.response) {
                    return _error.response;
                } else {
                    throw _error;
                }
            })
            .then((_response: AxiosResponse) => {
                return this.processGetActiveSurvey(_response);
            });
    }

    getActiveSurveyForUser(signal?: AbortSignal) {
        let url_ = this.baseUrl + "/api/v1/survey/activeForUser";
        url_ = addTimestampToUrl(url_);
        url_ = url_.replace(/[?&]$/, "");
        const options_: AxiosRequestConfig = {
            method: "GET",
            url: url_,
            headers: { "Accept": "text/plain" },
            signal
        };
        return this.instance.request(options_)
            .catch((_error: unknown) => {
                if (isAxiosError(_error) && _error.response) {
                    return _error.response;
                } else {
                    throw _error;
                }
            })
            .then((_response: AxiosResponse) => {
                return this.processGetActiveSurveyForUser(_response);
            });
    }

    getActiveSurveyId(signal?: AbortSignal) {
        let url_ = this.baseUrl + "/api/v1/survey/active/id";
        url_ = addTimestampToUrl(url_);
        url_ = url_.replace(/[?&]$/, "");
        const options_: AxiosRequestConfig = {
            method: "GET",
            url: url_,
            headers: { "Accept": "text/plain" },
            signal
        };
        return this.instance.request(options_)
            .catch((_error: unknown) => {
                if (isAxiosError(_error) && _error.response) {
                    return _error.response;
                } else {
                    throw _error;
                }
            })
            .then((_response: AxiosResponse) => {
                return this.processGetActiveSurveyId(_response);
            });
    }

    getAllSurveyHeaders(signal?: AbortSignal) {
        let url_ = this.baseUrl + "/api/v1/survey/headers";
        url_ = addTimestampToUrl(url_);
        url_ = url_.replace(/[?&]$/, "");
        const options_: AxiosRequestConfig = {
            method: "GET",
            url: url_,
            headers: { "Accept": "text/plain" },
            signal
        };
        return this.instance.request(options_)
            .catch((_error: unknown) => {
                if (isAxiosError(_error) && _error.response) {
                    return _error.response;
                } else {
                    throw _error;
                }
            })
            .then((_response: AxiosResponse) => {
                return this.processGetAllSurveyHeaders(_response);
            });
    }

    getSurvey(id: string, signal?: AbortSignal) {
        let url_ = this.baseUrl + "/api/v1/survey/{id}";
        if (id === undefined || id === null)
            throw new Error("The parameter 'id' must be defined.");
        url_ = url_.replace("{id}", encodeURIComponent("" + id));
        url_ = addTimestampToUrl(url_);
        url_ = url_.replace(/[?&]$/, "");
        const options_: AxiosRequestConfig = {
            method: "GET",
            url: url_,
            headers: { "Accept": "text/plain" },
            signal
        };
        return this.instance.request(options_)
            .catch((_error: unknown) => {
                if (isAxiosError(_error) && _error.response) {
                    return _error.response;
                } else {
                    throw _error;
                }
            })
            .then((_response: AxiosResponse) => {
                return this.processGetSurvey(_response);
            });
    }

    getSurveyIdsByName(name?: string, signal?: AbortSignal) {
        let url_ = this.baseUrl + "/api/v1/survey/surveyidsbyname";
        url_ = addTimestampToUrl(url_);
        if (name !== undefined) {
            url_ += "&name=" + encodeURIComponent("" + name);
        }
        url_ = url_.replace(/[?&]$/, "");
        const options_: AxiosRequestConfig = {
            method: "GET",
            url: url_,
            headers: { "Accept": "text/plain" },
            signal
        };
        return this.instance.request(options_)
            .catch((_error: unknown) => {
                if (isAxiosError(_error) && _error.response) {
                    return _error.response;
                } else {
                    throw _error;
                }
            })
            .then((_response: AxiosResponse) => {
                return this.processGetSurveyIdsByName(_response);
            });
    }

    getNoOfReponses(id: string, signal?: AbortSignal) {
        // Correct path for the middleware endpoint that returns number of responses
        let url_ = this.baseUrl + "/api/v1/survey/noofreponses/{id}";
        if (id === undefined || id === null)
            throw new Error("The parameter 'id' must be defined.");
        url_ = url_.replace("{id}", encodeURIComponent("" + id));
        url_ = addTimestampToUrl(url_);
        url_ = url_.replace(/[?&]$/, "");
        const options_: AxiosRequestConfig = {
            method: "GET",
            url: url_,
            headers: { "Accept": "text/plain" },
            signal
        };
        return this.instance.request(options_)
            .catch((_error: unknown) => {
                if (isAxiosError(_error) && _error.response) {
                    return _error.response;
                } else {
                    throw _error;
                }
            })
            .then((_response: AxiosResponse) => {
                return this.processGetNoOfReponses(_response);
            });
    }

    // New helper: returns numeric count (or null) parsed from plain-text endpoint
    getNoOfReponsesCount(id: string, signal?: AbortSignal): Promise<number | null> {
        let url_ = this.baseUrl + "/api/v1/survey/noofreponses/{id}";
        if (id === undefined || id === null)
            throw new Error("The parameter 'id' must be defined.");
        url_ = url_.replace("{id}", encodeURIComponent("" + id));
        url_ = addTimestampToUrl(url_);
        url_ = url_.replace(/[?&]$/, "");
        const options_: AxiosRequestConfig = {
            method: "GET",
            url: url_,
            headers: { "Accept": "text/plain" },
            signal
        };
        return this.instance.request(options_)
            .catch((_error: unknown) => {
                if (isAxiosError(_error) && _error.response) {
                    return _error.response;
                } else {
                    throw _error;
                }
            })
            .then((_response: AxiosResponse) => {
                const raw = _response && _response.data;
                if (raw === null || raw === undefined) return null;

                // If the backend returns a plain number, return it
                if (typeof raw === 'number') return raw;

                // Convert whatever we got to a string and extract the first integer found.
                // This is intentionally simple and handles cases like:
                //  - plain numeric strings: "5"
                //  - responses like: "Ok 5"
                //  - objects: { count: 5 } -> "{\"count\":5}"
                try {
                    const s = typeof raw === 'string' ? raw : JSON.stringify(raw);
                    const m = s.match(/-?\d+/);
                    if (m) return parseInt(m[0], 10);
                } catch {
                    // fallthrough
                }

                return null;
            });
    }

    /**
     * Get the total number of registered users (wrapper returns a number parsed from plain text)
     */
    // New helper: returns numeric users count parsed from plain-text endpoint
    getNoOfUsersCount(signal?: AbortSignal): Promise<number | null> {
        let url_ = this.baseUrl + "/api/v1/user/noofusers";
        url_ = addTimestampToUrl(url_);
        url_ = url_.replace(/[?&]$/, "");
        const options_: AxiosRequestConfig = {
            method: "GET",
            url: url_,
            headers: { "Accept": "text/plain" },
            signal
        };
        return this.instance.request(options_)
            .catch((_error: unknown) => {
                if (isAxiosError(_error) && _error.response) {
                    return _error.response;
                } else {
                    throw _error;
                }
            })
            .then((_response: AxiosResponse) => {
                const raw = _response && _response.data;
                if (raw === null || raw === undefined) return null;

                if (typeof raw === 'number') return raw;
                try {
                    const s = typeof raw === 'string' ? raw : JSON.stringify(raw);
                    const m = s.match(/-?\d+/);
                    if (m) return parseInt(m[0], 10);
                } catch {
                    // fallthrough
                }
                return null;
            });
    }

    getAllUserRoleAssignments(signal?: AbortSignal) {
        let url_ = this.baseUrl + "/api/v1/user/data/userroleassignments";
        url_ = addTimestampToUrl(url_);
        url_ = url_.replace(/[?&]$/, "");
        const options_: AxiosRequestConfig = {
            method: "GET",
            url: url_,
            headers: { "Accept": "text/plain" },
            signal
        };
        return this.instance.request(options_)
            .catch((_error: unknown) => {
                if (isAxiosError(_error) && _error.response) {
                    return _error.response;
                } else {
                    throw _error;
                }
            })
            .then((_response: AxiosResponse) => {
                return this.processGetAllUserRoleAssignments(_response);
            });
    }
    
    getAllRoleNames(signal?: AbortSignal): Promise<string[]> {
        let url_ = this.baseUrl + "/api/v1/clientconfigurations/roles";
        url_ = addTimestampToUrl(url_);
        url_ = url_.replace(/[?&]$/, "");
        const options_: AxiosRequestConfig = {
            method: "GET",
            url: url_,
            headers: { "Accept": "text/plain" },
            signal
        };
        return this.instance.request(options_)
            .catch((_error: unknown) => {
                if (isAxiosError(_error) && _error.response) {
                    return _error.response;
                } else {
                    throw _error;
                }
            })
            .then((_response: AxiosResponse) => {
                return this.processGetAllRoleNames(_response);
            });
    }

    getRoleConfiguration(role: string, signal?: AbortSignal): Promise<string> {
        let url_ = this.baseUrl + "/api/v1/clientconfigurations/roles/{role}";
        if (role === undefined || role === null)
            throw new Error("The parameter 'role' must be defined.");
        url_ = url_.replace("{role}", encodeURIComponent("" + role));
        url_ = addTimestampToUrl(url_);
        url_ = url_.replace(/[?&]$/, "");
        const options_: AxiosRequestConfig = {
            method: "GET",
            url: url_,
            headers: { "Accept": "text/plain" },
            signal
        };
        return this.instance.request(options_)
            .catch((_error: unknown) => {
                if (isAxiosError(_error) && _error.response) {
                    return _error.response;
                } else {
                    throw _error;
                }
            })
            .then((_response: AxiosResponse) => {
                return this.processGetRoleConfiguration(_response);
            });
    }

    getSurveyHeader(id: string, signal?: AbortSignal) {
        let url_ = this.baseUrl + "/api/v1/survey/header/{id}";
        if (id === undefined || id === null)
            throw new Error("The parameter 'id' must be defined.");
        url_ = url_.replace("{id}", encodeURIComponent("" + id));
        url_ = addTimestampToUrl(url_);
        url_ = url_.replace(/[?&]$/, "");
        const options_: AxiosRequestConfig = {
            method: "GET",
            url: url_,
            headers: { "Accept": "text/plain" },
            signal
        };
        return this.instance.request(options_)
            .catch((_error: unknown) => {
                if (isAxiosError(_error) && _error.response) {
                    return _error.response;
                } else {
                    throw _error;
                }
            })
            .then((_response: AxiosResponse) => {
                return this.processGetSurveyHeader(_response);
            });
    }

    improvePrompt(body?: ImprovePromptRequest, signal?: AbortSignal): Promise<ImprovePromptResponse> {
        let url_ = this.baseUrl + "/api/v1/prompt/improve";
        url_ = addTimestampToUrl(url_);
        url_ = url_.replace(/[?&]$/, "");
        const options_: AxiosRequestConfig = {
            method: "POST",
            url: url_,
            headers: { 
                "Accept": "application/json",
                "Content-Type": "application/json"
            },
            data: JSON.stringify(body),
            signal
        };
        return this.instance.request(options_)
            .catch((_error: unknown) => {
                if (isAxiosError(_error) && _error.response) {
                    return _error.response;
                } else {
                    throw _error;
                }
            })
            .then((_response: AxiosResponse) => {
                return super.processImprovePrompt(_response);
            });
    }

}
